/**
 * Helpers for the Grey Market Premium page: adapts GET /api/market/gmp items into
 * board rows, and provides filters, sorting, summary stats and CSV export.
 */
import { cleanText, isBlank, splitDateField, toGmpSlug, toSignedNumber, toUpperPrice } from './gmpDataMapper';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const RATING_LABELS = ['Unrated', 'Low', 'Moderate', 'Positive', 'Strong', 'Bullish'];
const CLOSED_STATUSES = ['closed', 'allotted', 'listed'];

const pad = (n) => String(n).padStart(2, '0');

export const toLocalIsoDate = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

// Source dates have no year ("24-Sep"); pick the year that lands closest to `now`
export const inferIsoDate = (dayMonth, now = new Date()) => {
  const match = /^(\d{1,2})[-\s]([A-Za-z]{3})/.exec(dayMonth || '');
  if (!match) return null;
  const month = MONTHS.findIndex((m) => m.toLowerCase() === match[2].toLowerCase());
  if (month === -1) return null;
  const day = Number(match[1]);
  const year = now.getFullYear();
  const closest = [year - 1, year, year + 1]
    .map((y) => new Date(y, month, day))
    .reduce((best, candidate) =>
      Math.abs(candidate - now) < Math.abs(best - now) ? candidate : best
    );
  return toLocalIsoDate(closest);
};

export const normalizeStatus = (rawStatus, closeOn, todayIso) => {
  const text = (rawStatus || '').toLowerCase();
  if (text.includes('closing')) return 'closing_today';
  if (text.includes('allot')) return 'allotted';
  if (text.includes('list')) return 'listed';
  if (text.includes('close')) return 'closed';
  if (/open|live|active/.test(text)) return closeOn && closeOn === todayIso ? 'closing_today' : 'open';
  if (/upcoming|forthcoming/.test(text)) return 'upcoming';
  return 'other';
};

// "₹405.00 Cr" -> 405, "₹90 Lakh" -> 0.9
const parseIssueSizeCr = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (isBlank(value)) return null;
  const amount = toUpperPrice(value);
  if (amount === null) return null;
  return /lakh|lac/i.test(String(value)) ? amount / 100 : amount;
};

export const mapApiItemToBoardRow = (item, index = 0, now = new Date()) => {
  const name = cleanText(item?.company_name) || 'Unnamed IPO';
  const open = splitDateField(item?.open_date);
  const close = splitDateField(item?.close_date);
  const closeOn = inferIsoDate(close.date, now);
  const fireCount = Math.min(5, (String(item?.rating || '').match(/🔥/gu) || []).length);
  const lotSize = toSignedNumber(item?.lot_size);

  return {
    id: `${toGmpSlug(name)}-${index}`,
    slug: cleanText(item?.company_name) ? toGmpSlug(item.company_name) : null,
    name,
    board: /sme/i.test(item?.board || '') ? 'SME' : 'Mainboard',
    exchange: cleanText(item?.exchange),
    status: normalizeStatus(item?.status, closeOn, toLocalIsoDate(now)),
    statusLabel: cleanText(item?.status),
    price: toUpperPrice(item?.price),
    gmp: toSignedNumber(item?.gmp_value),
    sourceGmpPercent: toSignedNumber(item?.gmp_percent),
    gmpDayLow: toSignedNumber(item?.gmp_day_low),
    gmpDayHigh: toSignedNumber(item?.gmp_day_high),
    fireRating: fireCount,
    subscription: toSignedNumber(item?.subscription),
    sizeCr: parseIssueSizeCr(item?.issue_size),
    lotSize: lotSize !== null ? Math.round(lotSize) : null,
    openOn: inferIsoDate(open.date, now),
    closeOn,
    boaOn: inferIsoDate(splitDateField(item?.boa_date).date, now),
    listingOn: inferIsoDate(splitDateField(item?.listing_date).date, now),
    openDayGmp: open.gmp,
    closeDayGmp: close.gmp,
    updatedOn: cleanText(item?.source_updated_on),
  };
};

export const withDerivedFields = (row) => {
  const hasGmp = row.gmp !== null && row.gmp !== undefined;
  const hasPrice = row.price !== null && row.price !== undefined;
  let gmpPercent = null;
  if (hasGmp) {
    gmpPercent = row.sourceGmpPercent ?? (hasPrice && row.price ? (row.gmp / row.price) * 100 : null);
  }
  return {
    ...row,
    hasGmp,
    gmpPercent,
    estListing: hasGmp && hasPrice ? row.price + row.gmp : null,
    isOpen: row.status === 'open' || row.status === 'closing_today',
    isClosed: CLOSED_STATUSES.includes(row.status),
    sentiment: hasGmp ? RATING_LABELS[row.fireRating] : 'Awaiting GMP',
  };
};

export const mapApiItemsToBoardRows = (items, now = new Date()) =>
  (Array.isArray(items) ? items : [])
    .filter(Boolean)
    .map((item, index) => withDerivedFields(mapApiItemToBoardRow(item, index, now)));

export const formatInr = (value, digits = 0) =>
  `₹${Number(value).toLocaleString('en-IN', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`;

export const formatSignedInr = (value) => {
  if (value > 0) return `+${formatInr(value)}`;
  if (value < 0) return `−${formatInr(Math.abs(value))}`;
  return formatInr(0);
};

export const formatSignedPercent = (value, digits = 2) => {
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${sign}${Math.abs(value).toFixed(digits)}%`;
};

// '2026-09-15' -> '15-Sep' (string split avoids timezone shifts)
export const formatShortDate = (iso) => {
  if (!iso) return '—';
  const [, month, day] = iso.split('-');
  return `${day}-${MONTHS[Number(month) - 1]}`;
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// '2026-09-24' -> 'Thu, 24 Sep 2026' (built by hand to match the short '24-Sep' style)
export const formatLongDate = (iso) => {
  if (!iso) return null;
  const [year, month, day] = iso.split('-').map(Number);
  const weekday = WEEKDAYS[new Date(year, month - 1, day).getDay()];
  return `${weekday}, ${day} ${MONTHS[month - 1]} ${year}`;
};

// Where an ISO date sits relative to today: 'done' | 'today' | 'upcoming' | 'unknown'
export const getDateState = (iso, now = new Date()) => {
  if (!iso) return 'unknown';
  const today = toLocalIsoDate(now);
  if (iso < today) return 'done';
  return iso === today ? 'today' : 'upcoming';
};

export const formatRefreshedAt = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

export const STATUS_META = {
  open: { label: 'Open', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  closing_today: { label: 'Closing Today', className: 'bg-rose-50 text-rose-600 border-rose-200' },
  closed: { label: 'Closed', className: 'bg-slate-100 text-slate-600 border-slate-200' },
  allotted: { label: 'Allotted', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  listed: { label: 'Listed', className: 'bg-violet-50 text-violet-700 border-violet-200' },
  upcoming: { label: 'Upcoming', className: 'bg-sky-50 text-sky-700 border-sky-200' },
  other: { label: 'Other', className: 'bg-slate-100 text-slate-600 border-slate-200' },
};

export const BOARD_FILTERS = [
  { key: 'all', label: 'All Issues', test: () => true },
  { key: 'active', label: 'Only Active GMP', test: (row) => row.hasGmp },
  { key: 'mainboard', label: 'Mainboard', test: (row) => row.board === 'Mainboard' },
  { key: 'sme', label: 'SME', test: (row) => row.board === 'SME' },
  { key: 'open', label: 'Open', test: (row) => row.isOpen },
  { key: 'closed', label: 'Closed', test: (row) => row.isClosed },
  { key: 'closing_today', label: 'Closing Today', test: (row) => row.status === 'closing_today', urgent: true },
  { key: 'upcoming', label: 'Upcoming', test: (row) => row.status === 'upcoming' },
];

export const isSmeRow = (row) => row.board === 'SME';

// SME GMP page (/sme-ipo-gmp): the board is already limited to SME issues
export const SME_BOARD_FILTERS = [
  { key: 'all', label: 'All SME Issues', test: () => true },
  { key: 'active', label: 'Only Active GMP', test: (row) => row.hasGmp },
  { key: 'nse', label: 'NSE Emerge', test: (row) => /nse/i.test(row.exchange || '') },
  { key: 'bse', label: 'BSE SME', test: (row) => /bse/i.test(row.exchange || '') },
  { key: 'open', label: 'Open', test: (row) => row.isOpen },
  { key: 'closing_today', label: 'Closing Today', test: (row) => row.status === 'closing_today', urgent: true },
  { key: 'upcoming', label: 'Upcoming', test: (row) => row.status === 'upcoming' },
  { key: 'closed', label: 'Closed', test: (row) => row.isClosed },
];

const isMissing = (value) => value === null || value === undefined;

// Descending with missing values last
const byDesc = (get) => (a, b) => {
  const x = get(a);
  const y = get(b);
  if (isMissing(x)) return isMissing(y) ? 0 : 1;
  if (isMissing(y)) return -1;
  return y - x;
};

// Ascending ISO dates with missing values last
const byDateAsc = (get) => (a, b) => {
  const x = get(a);
  const y = get(b);
  if (!x) return y ? 1 : 0;
  if (!y) return -1;
  return x.localeCompare(y);
};

export const SORT_OPTIONS = [
  { key: 'gmp_percent', label: 'Highest GMP (%)', compare: byDesc((row) => row.gmpPercent) },
  { key: 'gmp_value', label: 'Highest GMP (₹)', compare: byDesc((row) => row.gmp) },
  { key: 'subscription', label: 'Highest Subscription', compare: byDesc((row) => row.subscription) },
  { key: 'size', label: 'Largest Issue Size', compare: byDesc((row) => row.sizeCr) },
  { key: 'open_date', label: 'Opening Date', compare: byDateAsc((row) => row.openOn) },
];

export const BULLISH_THRESHOLD = 15;

export const computeBoardStats = (rows) => {
  const quoted = rows.filter((row) => row.hasGmp && row.gmpPercent !== null);
  const openQuoted = quoted.filter((row) => row.isOpen);

  const top = quoted.reduce(
    (best, row) => (!best || row.gmpPercent > best.gmpPercent ? row : best),
    null
  );

  // Average open-issue premium, weighted by issue size where the size is known
  const sized = openQuoted.filter((row) => row.sizeCr);
  const openWeight = sized.reduce((sum, row) => sum + row.sizeCr, 0);
  let avgOpenPremium = null;
  if (openWeight) {
    avgOpenPremium = sized.reduce((sum, row) => sum + row.gmpPercent * row.sizeCr, 0) / openWeight;
  } else if (openQuoted.length) {
    avgOpenPremium = openQuoted.reduce((sum, row) => sum + row.gmpPercent, 0) / openQuoted.length;
  }

  const bullishCount = quoted.filter((row) => row.gmpPercent >= BULLISH_THRESHOLD).length;
  const avgFireRating = quoted.length
    ? quoted.reduce((sum, row) => sum + row.fireRating, 0) / quoted.length
    : null;

  return {
    total: rows.length,
    mainboardCount: rows.filter((row) => row.board === 'Mainboard').length,
    smeCount: rows.filter((row) => row.board === 'SME').length,
    nseCount: rows.filter((row) => /nse/i.test(row.exchange || '')).length,
    bseCount: rows.filter((row) => /bse/i.test(row.exchange || '')).length,
    quotedCount: quoted.length,
    top,
    avgOpenPremium,
    openQuotedCount: openQuoted.length,
    bullishCount,
    bullishShare: quoted.length ? (bullishCount / quoted.length) * 100 : null,
    avgFireRating,
  };
};

const csvCell = (value) => {
  if (value === null || value === undefined) return '';
  const text = String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const buildBoardCsv = (rows) => {
  const header = [
    'Company', 'Board', 'Exchange', 'Status', 'Price (INR)', 'GMP (INR)', 'GMP (%)',
    'Est. Listing (INR)', 'GMP Day Low (INR)', 'GMP Day High (INR)', 'Subscription (x)',
    'Issue Size (INR Cr)', 'Lot Size', 'Open', 'Close', 'Basis of Allotment', 'Listing',
    'Fire Rating', 'Source Updated',
  ];
  const lines = rows.map((row) =>
    [
      row.name,
      row.board,
      row.exchange,
      row.statusLabel || STATUS_META[row.status]?.label,
      row.price,
      row.gmp,
      row.gmpPercent !== null ? row.gmpPercent.toFixed(2) : null,
      row.estListing,
      row.gmpDayLow,
      row.gmpDayHigh,
      row.subscription,
      row.sizeCr,
      row.lotSize,
      row.openOn,
      row.closeOn,
      row.boaOn,
      row.listingOn,
      row.fireRating,
      row.updatedOn,
    ]
      .map(csvCell)
      .join(',')
  );
  return [header.map(csvCell).join(','), ...lines].join('\r\n');
};

// Page numbers with gaps, e.g. [1, 2, 3, '…', 6]
export const buildPageList = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current - 1, current, current + 1]);
  if (current <= 3) [2, 3, 4].forEach((p) => pages.add(p));
  if (current >= total - 2) [total - 3, total - 2, total - 1].forEach((p) => pages.add(p));
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  return sorted.flatMap((p, i) => (i > 0 && p - sorted[i - 1] > 1 ? ['…', p] : [p]));
};

// Plain-language summary of one company's GMP position, built only from API fields
export const buildGmpSummary = (row, now = new Date()) => {
  const lines = [];
  const updated = row.updatedOn ? `, updated ${row.updatedOn}` : '';
  const subscribed = row.subscription !== null ? `${row.subscription.toFixed(2)}x` : null;
  const openDate = formatLongDate(row.openOn);
  const closeDate = formatLongDate(row.closeOn);
  const boaDate = formatLongDate(row.boaOn);
  const listingDate = formatLongDate(row.listingOn);
  const listingDone = getDateState(row.listingOn, now) === 'done';

  if (row.hasGmp) {
    lines.push(`${row.name} IPO last recorded a grey market premium (GMP) of ${formatSignedInr(row.gmp)}${updated}.`);
    if (row.estListing !== null) {
      const outlook =
        row.gmpPercent !== null
          ? `, an expected ${row.gmp < 0 ? 'loss' : 'gain'} of ${Math.abs(row.gmpPercent).toFixed(2)}% per share`
          : '';
      lines.push(
        `With an upper price band of ${formatInr(row.price)}, the estimated listing price is ${formatInr(row.estListing)} (cap price + latest GMP)${outlook}.`
      );
    } else {
      lines.push('The price band has not been announced yet, so a listing estimate is not available.');
    }
  } else {
    lines.push(
      `${row.name} IPO has no grey market quote yet${row.updatedOn ? ` as of ${row.updatedOn}` : ''}. GMP usually appears in the days just before an issue opens.`
    );
  }

  const listingClause = listingDate
    ? listingDone
      ? `shares listed on ${listingDate}`
      : `listing is scheduled for ${listingDate}`
    : null;

  if (row.status === 'upcoming' && openDate) {
    lines.push(
      `Bidding opens on ${openDate}${closeDate ? ` and closes on ${closeDate}` : ''}${listingClause ? `; ${listingClause}` : ''}.`
    );
  } else if (row.status === 'open') {
    lines.push(
      `The issue is open for bidding${closeDate ? ` until ${closeDate}` : ''}${subscribed ? ` and is subscribed ${subscribed} so far` : ''}.`
    );
  } else if (row.status === 'closing_today') {
    lines.push(`Bidding closes today${subscribed ? `, with the issue subscribed ${subscribed} so far` : ''}.`);
  } else if (row.status === 'allotted') {
    lines.push(
      `The basis of allotment was finalised${boaDate ? ` on ${boaDate}` : ''}${listingClause ? `; ${listingClause}` : ''}.`
    );
  } else if (row.status === 'closed' || row.status === 'listed') {
    lines.push(
      `Bidding closed${closeDate ? ` on ${closeDate}` : ''}${subscribed ? ` with the issue subscribed ${subscribed}` : ''}${listingClause ? `; ${listingClause}` : ''}.`
    );
  }

  const dayQuotes = [];
  if (row.openDayGmp !== null && row.openDayGmp !== undefined) {
    dayQuotes.push(`${formatSignedInr(row.openDayGmp)} on the opening day`);
  }
  if (row.closeDayGmp !== null && row.closeDayGmp !== undefined) {
    dayQuotes.push(`${formatSignedInr(row.closeDayGmp)} on the closing day`);
  }
  if (dayQuotes.length) lines.push(`The GMP stood at ${dayQuotes.join(' and ')}.`);

  return lines;
};
